import { getIconPath } from '@/common'
import Data from '@/data'
import Trans from '@/data/trans'
import { Range } from '@/data/types'
import { TooltipLine } from '@/types/common'
import { useAlt } from '@/ui/modifiers'
import { replaceReact } from '@/utils/format'
import classNames from 'classnames'
import { ReactNode } from 'react'

export const stripMarkup = (text: string) =>
  text
    .replace(/\[([\w\-/:]+)(?:\|([^\]]*))?\]/g, (_, id, text) => text ?? id)
    .replace(/<([^>]+)>{([^}]+)}/g, (_, style, text) => text)

export const markupString = (string: string, textFunc?: (text: string) => ReactNode) =>
  replaceReact(
    string,
    /<([^>]+)>{([^}]+)}|<<(\w+)>>|(\r?\n)/g,
    (_, style, text, glyph, brk) => {
      if (brk) {
        return <br />
      } else if (glyph) {
        return <span className={`poe-glyph poe-${glyph}`} />
      } else {
        const m = style.match(/^size:(\d+)$/)
        const props: React.HTMLAttributes<HTMLSpanElement> = {}
        if (m) props.style = { fontSize: `${(m[1] / 2).toFixed(1)}px` }
        else props.className = `poe-${style}`
        return <span {...props}>{textFunc ? textFunc(text) : text}</span>
      }
    },
    textFunc
  )

export const FormatLine = ({ text, type, highlight }: TooltipLine) => (
  <div className={classNames(type && `poe-${type}`)}>
    <span className={classNames('poe-lc', highlight && 'poe-highlight')}>{markupString(text)}</span>
  </div>
)

export const FormatLineBr = ({ text, type, highlight }: TooltipLine) =>
  text.split(/<br\/?>/).map((line, index) => <FormatLine key={index} type={type} highlight={highlight} text={line} />)

export const FormatLines = ({ lines }: { lines: TooltipLine[] }) =>
  lines.map((line, index) => <FormatLine type={line.type} text={line.text} highlight={line.highlight} key={index} />)

export function statLines(
  type: string,
  stats: Record<string, number | Range>,
  domain = 'global',
  mask?: number,
  prefix?: string,
  reminderStrings?: Set<string>
) {
  if (!stats) return []
  const reminders: string[][] = []
  const lines = Trans[domain].formatStats(stats, false, false, reminders)

  const result: TooltipLine[] = []
  lines.forEach((text, index) => {
    if (prefix) text = prefix + text
    const line: TooltipLine = { text, type }
    if (mask && mask & (1 << index)) line.highlight = true
    result.push(line)
    if (reminders && reminders[index]) {
      for (const str of reminders[index]) {
        if (reminderStrings) {
          reminderStrings.add(str)
        } else {
          result.push({
            text: Data.reminder_text[str],
            type: 'reminderString'
          })
        }
      }
    }
  })
  return result
}

export function GameTooltip({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const altKey = useAlt()
  return (
    <div className={classNames('poe-itemPopup', { 'poe-AltKey': altKey }, className)} {...props}>
      {children}
    </div>
  )
}

export function TooltipImage({ name, icon, width }: { name: string; icon: string; width?: number }) {
  if (!icon) return null
  return <img src={getIconPath(icon)} alt={name} className={width ? `poe-iw${width}` : undefined} />
}
